import _ from 'lodash'
import React from 'react'
import classNames from 'classnames'
import { Classes, Icon, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import apis from 'browser/app/models/apis'
import { DEBOUNCE_TIMEOUT } from 'browser/app/utils/utils'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { IComponentsContext } from 'browser/contexts/components/components-context'
import MobileComponents from '../index'
import { ViewRenderer } from 'shared-libs/components/view/renderer'
import { MultiMixinRenderer } from 'shared-libs/components/entity/multi-mixin-renderer'
import { Entity } from 'shared-libs/models/entity'
import { Query } from 'shared-libs/models/query'
import { evaluateExpression, evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import './_mobile-entity-search-field.scss'
import { globalTranslationTable } from 'browser/mobile/util/global-translation-table'
import { translateString } from 'shared-libs/helpers/utils'

import {
  DEFAULT_SEARCH_ORDER,
  IMobileEntitySearchFieldProps,
  IResolvedSchemaPaths,
  ISelectionPayload,
  NUM_SEARCH_RESULTS_TO_SHOW,
} from './_utils'

interface IMobileEntitySearchFieldOverlayProps {
  fieldProps: IMobileEntitySearchFieldProps
  componentsContext: IComponentsContext
  entitySchema: any
  entityDisplayName: string
  entityDisplayNamePlural: string
  schemaPaths: IResolvedSchemaPaths
  onCommitSelection: (payload: ISelectionPayload) => void
  onClose?: () => void
}

interface IMobileEntitySearchFieldOverlayState {
  searchFilter: string
  entities: Entity[]
  isSearching: boolean
  selectedItem?: Entity
}

export class MobileEntitySearchFieldOverlay extends React.Component<
  IMobileEntitySearchFieldOverlayProps,
  IMobileEntitySearchFieldOverlayState
> {
  private collection: any
  private debouncedQuery: any

  constructor(props: IMobileEntitySearchFieldOverlayProps) {
    super(props)

    this.collection = undefined
    this.debouncedQuery = _.debounce(this.queryEntities, DEBOUNCE_TIMEOUT)

    this.state = {
      searchFilter: '',
      entities: [],
      isSearching: false,
      selectedItem: undefined,
    }
  }

  public componentDidMount(): void {
    const searchFilter = this.state.searchFilter
    if (this.isSearchValid(searchFilter)) {
      this.setState({ isSearching: true })
      this.queryEntities(searchFilter)
    }
  }

  public componentWillUnmount(): void {
    this.debouncedQuery.cancel()
    this.collection?.cancel()
    this.collection?.dispose()
  }

  public render() {
    return (
      <div className="entity-search__overlay">
        {this.renderHeader()}
        {this.renderSearchContent()}
        {this.renderConfirmBar()}
      </div>
    )
  }

  // Pinned commit bar for confirmOnSelect mode. The user highlights a result card (which sets
  // selectedItem) and commits it here. Hidden unless result rows are actually shown.
  private renderConfirmBar = () => {
    const { confirmOnSelect } = this.props.fieldProps
    const { selectedItem, entities, isSearching, searchFilter } = this.state
    const hasVisibleResults =
      !isSearching && !_.isEmpty(entities) && this.isSearchValid(searchFilter)
    if (!confirmOnSelect || !hasVisibleResults) {
      return null
    }
    return (
      <div className="entity-search__confirm-bar">
        <Button
          className="entity-search__confirm-button"
          intent={Intent.PRIMARY}
          isDisabled={_.isNil(selectedItem)}
          onClick={this.handleConfirmSelection}
        >
          {this.resolveConfirmLabel()}
        </Button>
      </div>
    )
  }

  private renderHeader = () => {
    const { searchFilter } = this.state
    return (
      <header className="entity-search__header">
        <div className="flex flex-row justify-between items-center pv2 ph3">
          <span />
          <div className="flex flex-column items-center">
            <h1 className="mobile-header__title">{this.getHeaderTitle()}</h1>
            {this.props.fieldProps.searchModalSubtitle && (
              <p className="mt0 mb0 f6 entity-search__header-subtitle" style={{ color: 'white' }}>
                {this.props.fieldProps.searchModalSubtitle}
              </p>
            )}
          </div>
          <Button
            className={classNames(Classes.MINIMAL, 'mobile-header__button')}
            onClick={this.handleClose}
          >
            <Icon
              icon={IconNames.CROSS}
              className="entity-search__header-close-icon"
            />
          </Button>
        </div>
        <div className="entity-search__search-input-row ph4 pb3 flex items-center">
          <div className="entity-search__search-input-field w-100">
            <Icon
              icon={IconNames.SEARCH}
              className="entity-search__search-input-icon"
            />
            <input
              autoFocus={true}
              className="c-input mobile-control w-100 entity-search__search-input"
              placeholder={translateString('Entity_Search_Placeholder', globalTranslationTable, { entityDisplayNamePlural: _.lowerCase(this.props.entityDisplayNamePlural) })}
              value={searchFilter}
              onChange={this.handleSearchFilterChange}
            />
          </div>
        </div>
      </header>
    )
  }

  private renderSearchContent = () => {
    const { isSearching } = this.state

    return (
      <div className="entity-search__search-results">
        {isSearching ? (
          <div className="pt4">
            <LoadingSpinner />
          </div>
        ) : (
          this.renderSearchBody()
        )}
      </div>
    )
  }

  private renderSearchBody = () => {
    const { minQueryLength } = this.props.fieldProps
    const { searchFilter, entities } = this.state

    // No search term entered: show the help state rather than an empty "No results found for ''".
    // With preloadResults, any preloaded results are shown below; when there are none, the help
    // state ("enter an id or keyword to search") is the sensible empty state.
    if (_.isEmpty(searchFilter) && _.isEmpty(entities)) {
      return this.renderHelpState()
    }

    if (!this.isSearchValid(searchFilter)) {
      return (
        <div className="tc pa4 mt5">
          <Icon icon={IconNames.SEARCH} className="entity-search__state-icon" />
          <p className="mt2 f5 fw5 entity-search__state-text">
            {translateString('Entity_Search_Min_Characters', globalTranslationTable, { minQueryLength })}
          </p>
        </div>
      )
    }

    if (_.isEmpty(entities)) {
      return this.renderEmptyResultState()
    }

    return (
      <>
        <div className="entity-search__results-header">
          {searchFilter
            ? translateString('Entity_Search_Results_For', globalTranslationTable, { searchFilter })
            : translateString('Entity_Search_Results', globalTranslationTable)}
        </div>

        <div className="entity-search__results-content">
          {_.map(entities, (entity, index) => this.renderResultCard(entity, index))}
        </div>
      </>
    )
  }

  private renderHelpState = () => {
    const { help } = this.props.schemaPaths

    if (help && _.get(this.props.entitySchema, help)) {
      return (
        <ViewRenderer
          schema={_.get(this.props.entitySchema, help)}
          state={this.getRendererState()}
          componentsMap={MobileComponents}
          api={apis}
        />
      )
    }

    return (
      <div className="tc pa4 mt5">
        <Icon icon={IconNames.SEARCH} className="entity-search__state-icon" />
        <p className="mt2 f5 fw5 entity-search__state-text">
          {translateString('Entity_Search_Help_Text', globalTranslationTable, { entityDisplayNamePlural: _.lowerCase(this.props.entityDisplayNamePlural) })}
        </p>
      </div>
    )
  }

  private renderEmptyResultState = () => {
    const { emptyResult } = this.props.schemaPaths

    if (emptyResult && _.get(this.props.entitySchema, emptyResult)) {
      return (
        <ViewRenderer
          schema={_.get(this.props.entitySchema, emptyResult)}
          state={this.getRendererState()}
          componentsMap={MobileComponents}
          api={apis}
        />
      )
    }

    return (
      <div className="tc pa4 mt4">
        <Icon icon={IconNames.SMALL_CROSS} className="entity-search__state-icon" />
        <p className="mt2 f5 fw5 entity-search__state-text">
          {translateString('Entity_Search_No_Results', globalTranslationTable, { searchFilter: this.state.searchFilter })}
        </p>
      </div>
    )
  }

  private renderResultCard = (entity: Entity, index: number) => {
    const { selectedItem } = this.state
    const { item } = this.props.schemaPaths
    const { fieldProps } = this.props

    const selectOption = () => this.handleSelectEntity(entity)
    const isSelected = selectedItem?.uniqueId === entity?.uniqueId

    let content: React.ReactNode

    if (fieldProps.renderItem) {
      content = fieldProps.renderItem({
        entity,
        item: entity,
        selectedItem,
        selectOption,
      })
    } else if (item && _.get(this.props.entitySchema, item)) {
      content = (
        <MultiMixinRenderer
          actions={this}
          componentsMap={MobileComponents}
          context={{
            density: 'collapse',
            isHorizontalLayout: false,
            isStatic: true,
          }}
          state={this.getItemRendererState(entity)}
          value={entity}
          uiSchemaPath={item}
          shouldOverrideUiSchema={true}
        />
      )
    } else {
      content = (
        <div className="entity-search__result-card-fallback-label">
          {entity?.displayName}
        </div>
      )
    }

    return (
      <div
        key={entity?.uniqueId || index}
        onClick={selectOption}
        className={classNames('entity-search__result-card', {
          'entity-search__result-card--selected': isSelected,
        })}
      >
        {content}
      </div>
    )
  }

  private handleSearchFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchFilter = event.target.value

    this.setState({
      searchFilter,
      selectedItem: undefined,
    })

    const { rawQueryPath, onChange } = this.props.fieldProps
    if (rawQueryPath) {
      (onChange as any)?.(searchFilter || null, false, rawQueryPath)
    }

    this.collection?.cancel()

    if (!this.isSearchValid(searchFilter)) {
      this.debouncedQuery.cancel()
      this.setState({ entities: [], isSearching: false })
      return
    }

    this.setState({ isSearching: true })
    this.debouncedQuery(searchFilter)
  }

  private handleSelectEntity = async (entity: Entity) => {
    // In confirmOnSelect mode, tapping a card only highlights it; commit happens via the confirm bar.
    if (this.props.fieldProps.confirmOnSelect) {
      this.setState({ selectedItem: entity })
      return
    }

    this.setState({ selectedItem: entity })

    const refreshedItem = await this.refreshEntity(entity)

    this.props.onCommitSelection({
      entity: refreshedItem,
      searchFilter: this.state.searchFilter,
    })
    this.props.onClose?.()
  }

  private handleConfirmSelection = async () => {
    const { selectedItem, searchFilter } = this.state
    if (_.isNil(selectedItem)) {
      return
    }

    const refreshedItem = await this.refreshEntity(selectedItem)

    this.props.onCommitSelection({
      entity: refreshedItem,
      searchFilter,
    })
    this.props.onClose?.()
  }

  // Resolves the confirm button label from confirmButtonLabel. Evaluated against the parent workflow
  // context (via frames) plus `selectedItem`/`item` (the highlighted entity's data) and `searchFilter`,
  // so configs can reference both, e.g. "'Add ' + selectedItem.displayName + ' to ' + _transient.spotName".
  private resolveConfirmLabel = (): string => {
    const { confirmButtonLabel, frames } = this.props.fieldProps
    const { selectedItem, searchFilter } = this.state
    const fallback = `Select ${this.props.entityDisplayName}`
    if (!confirmButtonLabel) {
      return fallback
    }
    // Expose the highlighted entity's plain data (parity with how list item templates read entities).
    const selectedData = selectedItem
      ? { ...selectedItem.content, displayName: selectedItem.displayName }
      : undefined
    try {
      const scope = { ...CustomFormulas, selectedItem: selectedData, item: selectedData, searchFilter }
      const result = frames
        ? evaluateExpressionWithScopes(frames, confirmButtonLabel, scope)
        : evaluateExpression({ ...this.props.componentsContext, ...scope }, confirmButtonLabel)
      return _.isNil(result) || result === '' ? fallback : String(result)
    } catch {
      return fallback
    }
  }

  private handleClose = () => {
    this.props.onClose?.()
  }

  private queryEntities = async (searchFilter: string = '') => {
    const { query = {}, transformSearchFilterFn, transformResultsFn } = this.props.fieldProps
    const { entityType, queryPath, queryPaths, queryType } = query

    if (!entityType) {
      this.setState({ entities: [], isSearching: false })
      return
    }

    const queryInput = transformSearchFilterFn
      ? transformSearchFilterFn(searchFilter)
      : searchFilter

    const queryBuilder = new Query(apis)
      .setEntityType(entityType)
      .setQueryType(queryType)
      .setComputations(query?.computations)
      .setGroups(query?.groups)
      .setFilters(query?.filters)
      .setMetadata(query?.metadata)
      .setSize(NUM_SEARCH_RESULTS_TO_SHOW)
      .setOrders(query?.orders || DEFAULT_SEARCH_ORDER)
      .setQuery(queryInput)

    if (queryPaths) {
      queryBuilder.setQueryPaths(queryPaths)
    } else {
      queryBuilder.setQueryPath(queryPath)
    }

    this.collection?.cancel()
    this.collection?.dispose()

    this.collection = queryBuilder.getCollection()

    try {
      let entities = await this.collection.find()

      if (transformResultsFn) {
        // transformResultsFn may return a Promise (e.g. distance sort via GEOPOSITION()).
        entities = await Promise.resolve(transformResultsFn({ entities, searchQuery: searchFilter }))
      }

      this.setState({ entities })
    } catch (e) {
      if (this.isCancellationError(e)) {
        return
      }

      console.error('mobile-entity-search-field query failed', {
        error: e,
        searchFilter,
      })

      this.setState({ entities: [] })
    } finally {
      this.collection = undefined
      this.setState({ isSearching: false })
    }
  }

  private getItemRendererState = (entity: Entity) => {
    const state = this.getRendererState()
    const entityFieldKeys = Object.keys((entity as any)?.content ?? {})
    // Omit any state key that collides with the entity's own top-level fields so the entity always
    // wins inside its own card.
    return _.omit(state, entityFieldKeys)
  }

  private refreshEntity = async (entity: Entity) => {
    if (_.isNil(entity?.uniqueId)) {
      return entity
    }

    try {
      return await apis.getStore().findRecord(entity.uniqueId)
    } catch (e) {
      return entity
    }
  }

  private isSearchValid = (searchFilter: string) => {
    const { minQueryLength = 0, preloadResults } = this.props.fieldProps

    return preloadResults || (!_.isEmpty(searchFilter) && searchFilter.length >= minQueryLength)
  }

  private isCancellationError = (error: any) => {
    return (
      error?.name === 'CancellationError' ||
      error?.message?.includes('canceled') ||
      error?.message?.includes('cancelled') ||
      error?.message?.includes('aborted')
    )
  }

  private getHeaderTitle = () => {
    const { searchModalTitle } = this.props.fieldProps
    if (searchModalTitle) {
      return searchModalTitle
    }
    return translateString('Entity_Search_Find', globalTranslationTable, {
      entityDisplayName: _.capitalize(this.props.entityDisplayName),
    })
  }

  private getRendererState = () => {
    return {
      ...this.props.componentsContext,
      ...this.props.fieldProps,
      entities: this.state.entities,
      searchFilter: this.state.searchFilter,
      entityDisplayName: this.props.entityDisplayName,
    }
  }
}
